import { Opportunity, OpportunityFieldGenerationResponse } from "@/opportunity/opportunity";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import React, { Dispatch, SetStateAction, useState } from "react";
import { useOpportunityMutation } from "@/opportunity/api";
import { Spinner } from "@/components/ui/spinner";
import { Sparkles } from "lucide-react";
import { UseMutationResult } from "@tanstack/react-query";

export const UpdateFieldDialog = (
  {
    opportunity,
    opportunityKey,
    isOpen,
    setIsOpen,
    usesDefaultValue,
    generateValueMutation
  }: {
    opportunity: Opportunity,
    opportunityKey: keyof Opportunity,
    isOpen: boolean,
    setIsOpen: Dispatch<SetStateAction<boolean>>
    usesDefaultValue?: boolean,
    generateValueMutation?: UseMutationResult<OpportunityFieldGenerationResponse, Error, void, unknown>,
  }
) => {

  const { mutation, hasErrored } = useOpportunityMutation(
    opportunity,
    () => {
      setIsOpen(false)
    },
  )
  const [value, setValue] = useState(opportunity[opportunityKey] || "")
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);

  const typeOutValue = (newValue: string) => {
    const words = newValue.split(" ")

    let counter = 0;
    const interval = setInterval(
      () => {
        counter += 1;
        setValue(words.slice(0, counter).join(" "))
        if (counter == words.length) {
          clearInterval(interval);
        }
      },
      20
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Opportunity {opportunityKey}</DialogTitle>
          <DialogDescription className="flex flex-col gap-2">
            <p>
              Provide a new value for {opportunityKey}
            </p>
            {
              usesDefaultValue && (
                <p>
                  To update the default value for this field,
                  {' '}
                  <a
                    className="underline"
                    target="_blank"
                    href={`${import.meta.env.VITE_API_URL.replace(/\/api\/$/, "")}/admin/opportunities/opportunitycategory/${opportunity.category_object.id}/change/`}
                  >
                    edit the category in ThaloOS.
                  </a>
                </p>
              )
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter value here"
            rows={10}
            disabled={generateValueMutation?.isPending}
          />
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!opportunity[opportunityKey]}
              onClick={async () => {
                if (deleteConfirmed) {
                  await mutation.mutateAsync({ [opportunityKey]: null });
                  setValue(undefined);
                  setDeleteConfirmed(false);
                } else {
                  setDeleteConfirmed(true);
                }
              }}>
              {
                deleteConfirmed ? "Are you sure?" : "Delete"
              }
            </Button>
            {
              generateValueMutation && (
                <Button
                  onClick={async () => {
                    const res = await generateValueMutation.mutateAsync();
                    typeOutValue(res.output)
                  }}
                  disabled={generateValueMutation.isPending}
                  className="flex items-center gap-2"
                  variant="thalo"
                >
                  {
                    generateValueMutation.isPending ? (
                      <Spinner/>
                    ) : (
                      <Sparkles size={16}/>
                    )
                  }
                  Generate
                </Button>
              )
            }
            <Button
              onClick={async () => {
                await mutation.mutateAsync({ [opportunityKey]: value });
              }}
              disabled={mutation.isPending}
            >
              Save
            </Button>
          </div>
        </div>
        {
          hasErrored && (
            <Alert variant="warning" className="text-xs">Couldn't update. Please refresh the page and try again.</Alert>
          )
        }
        {
          generateValueMutation?.isError && (
            <Alert variant="warning" className="text-xs">Couldn't generate. Please refresh the page and try again.</Alert>
          )
        }
      </DialogContent>
    </Dialog>
  )
};